import axios from "axios";
import { BehaviorSubject } from "rxjs";
import { signInWithCustomToken, signOut as firebaseSignout, setPersistence, inMemoryPersistence } from "firebase/auth";
import { brandingFromService, loginUserToUser } from "../../models/user";
import { PasswordResetMissingTempPasswordError, PasswordResetError, NoAuthorizedUser, PasswordSetError, UnauthenticatedError } from "../../errors";
import { PasswordResetMode } from "../../models/authentication";
import { BrowserCredentialStorage } from "./credential-storage";
import { FilterTypes } from "../../handlers/filter";
import TenantSSO from "./tenantSSO";
export { TenantSSOError } from "./errors";
/**
 * Decodes a JWT payload without verifying it. The readers below take claims
 * that only pick a pool, a look or when to refresh, never anything that grants
 * access, so a malformed token is the exchange's problem to report rather than ours.
 * @param token JWT token string
 */
const decodeJwtPayload = (token) => {
    const payload = token?.split(".")[1];
    if (!payload) {
        return null;
    }
    try {
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const json = typeof atob === "function" ? atob(base64) : Buffer.from(base64, "base64").toString("binary");
        return JSON.parse(json);
    }
    catch {
        return null;
    }
};
/**
 * Reads the GCIP tenant a custom token was minted in, or null for the default
 * pool. Unverified on purpose: this only selects which pool to redeem against,
 * and the exchange itself is what authenticates the token.
 * @param token Firebase custom token
 */
export const tenantIdFromCustomToken = (token) => decodeJwtPayload(token)?.tenant_id ?? null;
/** Margin before a custom token's `exp` at which it counts as stale, absorbing client clock skew. */
const FIREBASE_CUSTOM_TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;
/**
 * Reads the organization branding claim from a JWT (an identity access token or
 * a Firebase custom token) and returns it in the frontend shape. The claim is
 * minted snake_case; nothing outside this function should see that spelling.
 * @param token JWT token string
 */
export const brandingFromCustomToken = (token) => {
    const payload = decodeJwtPayload(token);
    const branding = payload?.branding ?? payload?.claims?.branding;
    return branding ? brandingFromService(branding) : null;
};
/**
 * This is a special case collection that only handle auth.
 * Because of this it doesn't extend handler because there is no
 * really searching, deleting etc within auth.
 */
const LOGOUT_TIMEOUT_MS = 5000;
class Authentication {
    /**
     * Authentication needs to have a connection to firebase to
     * do all auth currently, and a reference to sdk.urls
     * @param sdk MadSDK
     */
    constructor(sdk) {
        this.sdk = sdk;
        // Bumped on sign-in and sign-out so a trySession that resolves afterwards
        // can tell its response belongs to a session that no longer exists.
        this.sessionGeneration = 0;
        this.madFire = sdk.madFire;
        this.tenantSSO = new TenantSSO(sdk);
        this.credentials = sdk.config.credentialStorage || new BrowserCredentialStorage();
        this.currentAccount = new BehaviorSubject(null);
        this.addAuthorizationHeader();
    }
    /**
     * Triggers the callback immedaitely with the current user state, and any time it changes in the future
     * @param callback
     */
    onAuthStateChanged(callback) {
        return this.currentAccount.subscribe(callback);
    }
    /**
     * @param account: the account to fetch latest user info for.
     * @return: that same account, with the user info updated to latest.
     */
    // TODO: BASE-776, delete and deprecate this
    async populateWithLatestUserData(account) {
        const id = account.user.id;
        const branding = account.user?.branding ??
            brandingFromCustomToken(account.madAuthToken) ??
            brandingFromCustomToken(account.firebaseCustomAuthToken) ??
            undefined;
        const latestUser = await this.sdk.users.find_once({
            where: [
                {
                    field: "id",
                    type: FilterTypes.EQ,
                    value: id
                }
            ]
        });
        if (!latestUser) {
            console.warn("Failed to populate account with latest user data.");
        }
        const user = latestUser ?? loginUserToUser(account.user);
        return branding ? { ...account, user: { ...user, branding } } : { ...account, user };
    }
    /**
     * Pushes the new user info to all subscribers.
     * Does nothing if account is currently null.
     * @param user: fresh user information.
     */
    // TODO: BASE-776, delete and deprecate this
    refreshUserInfo(user) {
        const currentAccount = this.getCurrentAccount();
        if (currentAccount === null) {
            return;
        }
        const updatedAccount = {
            ...currentAccount,
            user
        };
        this.currentAccount.next(updatedAccount);
    }
    /**
     * This method checks if we have access to the account endpoint
     * @returns {Promise<Account>}
     */
    async trySession(verify) {
        const ssoToken = this.credentials.cookie.get();
        if (ssoToken) {
            return this.loginWithSSOToken(ssoToken);
        }
        // A missing SSO cookie does not mean no session: identity establishes one
        // with an HttpOnly access_token cookie, which document.cookie cannot see.
        // Edge authenticates that cookie, so the account call is what decides. A
        // 401 still throws UnauthenticatedError, at the cost of one request for a
        // caller who really has no session.
        const generation = this.sessionGeneration;
        const isStale = () => generation !== this.sessionGeneration;
        // The token cookie is shared by every tab; another tab may have signed out
        // or switched users meanwhile, so only replace the value this call last saw or wrote.
        let ownToken = this.credentials.token.get();
        const tokenUnchanged = () => this.credentials.token.get() === ownToken;
        try {
            const { data: account } = await axios.get(`${this.sdk.urls.burnsBaseUrl}/account`);
            if (isStale()) {
                throw new UnauthenticatedError();
            }
            if (verify && !verify(account.user)) {
                await this.signOut();
                throw "mad-sdk login verify failed";
            }
            // /account mints a fresh token; storing it slides the session expiry for
            // active users. Burns may omit it, and a temporary session never persists.
            if (account.madAuthToken && !this.temporaryToken && tokenUnchanged()) {
                this.credentials.token.set(account.madAuthToken);
                ownToken = account.madAuthToken;
            }
            await this.authFirebaseWithToken(account.firebaseCustomAuthToken);
            const accountWithFreshUserData = await this.populateWithLatestUserData(account);
            if (isStale()) {
                throw new UnauthenticatedError();
            }
            this.currentAccount.next(accountWithFreshUserData);
            return accountWithFreshUserData;
        }
        catch (e) {
            if (!isStale() && tokenUnchanged()) {
                this.credentials.token.set(undefined);
            }
            throw new UnauthenticatedError();
        }
    }
    async loginWithSSOToken(token) {
        try {
            const { data: account } = await axios.post(`${this.sdk.urls.burnsBaseUrl}/login`, {
                access_token: token
            });
            return this.initializeUserSession(account);
        }
        catch (e) {
            this.credentials.cookie.clear();
            throw new UnauthenticatedError();
        }
    }
    /**
     * Attempts to authenticate a user by email and password.
     * @param email email to auth with.
     * @param password password for auth.
     * @param verify: if provided, an extra function to verify the user once signed in.
     * @throws: if login fails, or verify is provided and returns false.
     */
    async auth(email, password, verify) {
        try {
            const { data: account } = await axios.post(`${this.sdk.urls.burnsBaseUrl}/login`, {
                email,
                password
            });
            if (verify && !verify(account.user)) {
                await this.signOut();
                throw "mad-sdk login verify failed";
            }
            return this.initializeUserSession(account);
        }
        catch (e) {
            console.warn("mad-sdk login failed", e);
            throw e;
        }
    }
    /**
     * Attempts to authenticate a local development session.
     * @param email the email address of the org to log in as.
     * @param token the gcloud auth token of the developer logging in.
     * @throws: if login fails.
     */
    async localAuth(email, token) {
        try {
            const { data: account } = await axios.get(`${this.sdk.urls.burnsBaseUrl}/account`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { email }
            });
            return this.initializeUserSession(account);
        }
        catch (e) {
            console.warn("mad-sdk login failed", e);
            throw e;
        }
    }
    /**
     * Initializes the session for the user account that was successfully logged in
     * @param account The user account returned on successful login
     * @param isSessionTemporary Indicates if the session should be persisted in storage
     * @returns
     */
    async initializeUserSession(account, isSessionTemporary = false) {
        // A logout still in flight would clear the MAD-AUTH cookie this writes.
        await this.settleLogout();
        this.sessionGeneration++;
        if (isSessionTemporary) {
            const firebaseAuth = this.madFire.getAuth();
            await setPersistence(firebaseAuth, inMemoryPersistence);
            this.temporaryToken = account.madAuthToken;
        }
        else {
            this.credentials.token.set(account.madAuthToken);
        }
        await this.authFirebaseWithToken(account.firebaseCustomAuthToken);
        this.credentials.cookie.clear();
        const accountWithFreshUserData = await this.populateWithLatestUserData(account);
        this.currentAccount.next(accountWithFreshUserData);
        return accountWithFreshUserData;
    }
    /**
     * Allows authentication using a token instead of email/password
     * @param token Token to use for authentication
     */
    async authFirebaseWithToken(token) {
        const auth = this.madFire.getAuth();
        // A custom token minted inside a GCIP tenant can only be redeemed by an
        // Auth instance scoped to that same tenant; otherwise the exchange 400s.
        // Read it off the token rather than configuring it, so tokens from the
        // default pool (no tenant_id) keep working unchanged.
        //
        // Assigned unconditionally, including null: a default-pool login after a
        // tenant one would otherwise inherit the stale tenant and fail.
        if (auth) {
            auth.tenantId = tenantIdFromCustomToken(token);
        }
        return signInWithCustomToken(auth, token);
    }
    /**
     * Resolves once a logout started by {@link signOut} has finished. A login
     * that receives identity's cookies must wait on this, or the logout's late
     * response would clear the new session's cookies.
     * @internal
     */
    async settleLogout() {
        await this.pendingLogout;
    }
    /**
     * Signout the current user.
     */
    async signOut() {
        this.sessionGeneration++;
        // Only identity can clear its HttpOnly session cookies (set by a tenant SSO
        // login); JS cannot tell whether they exist, so every sign-out asks. The
        // bearer is captured before the local clear and the call runs in the
        // background, so a slow identity does not hold up sign-out. Logins wait on
        // it via settleLogout(), at most LOGOUT_TIMEOUT_MS.
        if (!this.pendingLogout) {
            const logoutConfig = this.buildAuthorizationHeaderConfig({
                withCredentials: true,
                timeout: LOGOUT_TIMEOUT_MS
            });
            this.pendingLogout = Promise.resolve()
                .then(() => axios.post(`${this.sdk.urls.identityBaseUrl}/auth/logout`, {}, logoutConfig))
                .then(() => undefined, () => undefined)
                .finally(() => {
                this.pendingLogout = undefined;
            });
        }
        const auth = this.madFire.getAuth();
        this.sdk.caches.clear();
        await firebaseSignout(auth);
        this.credentials.token.clear();
        this.credentials.cookie.clear();
        this.currentAccount.next(null);
    }
    /**
     * Used to change a password for a user.
     * @param password: the new password.
     * @param email: optional. The email of the user to set the password for. If undefined, operates on the currently-authenticated user.
     */
    async setPassword(password, email) {
        const route = email ? "admin" : "account";
        return new Promise((resolve, reject) => {
            axios
                .post(`${this.sdk.urls.burnsBaseUrl}/${route}/update-password`, {
                password,
                email
            }, {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(async () => resolve())
                .catch((error) => {
                if (error.response?.status === 403 || error.response?.status === 401) {
                    reject(new NoAuthorizedUser());
                }
                reject(new PasswordSetError());
            });
        });
    }
    /**
     * Reset password for a given email.
     * @param email: email to reset the password for.
     * @param mode: TEMP_PASSWORD sets and returns a password; EMAIL mails a reset
     *   link. Defaults to TEMP_PASSWORD, which is burns' historical behaviour.
     * @return: the temporary password, or "" in EMAIL mode.
     *
     * The two modes address different services, and that is deliberate.
     *
     * TEMP_PASSWORD keeps posting to the burns path, whose edge handler is
     * rollout-gated (IDENTITY_USER_CACHE_ADMIN_RESET_ROLLOUT_PERCENT) and falls
     * back to burns on an identity 5xx. Either service implements that mode
     * identically, so the gate is invisible to the caller.
     *
     * EMAIL addresses identity directly, bypassing the gate. burns'
     * ResetPasswordDto carries only `email` and its handler unconditionally
     * rotates the password, so it cannot model EMAIL and would silently rotate
     * instead. Routing EMAIL to identity only means an environment without
     * identity 404s rather than quietly changing a credential.
     */
    async resetPassword(email, mode = PasswordResetMode.TEMP_PASSWORD) {
        const url = mode === PasswordResetMode.EMAIL
            ? `${this.sdk.urls.identityBaseUrl}/admin/reset-password`
            : `${this.sdk.urls.burnsBaseUrl}/admin/reset-password`;
        return new Promise((resolve, reject) => {
            axios
                .post(url, {
                email,
                mode
            }, {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(async (res) => {
                // burns spells it tempPW, identity tempPassword.
                const tempPW = res.data?.tempPW || res.data?.tempPassword;
                // Email mode returns no password by design; only temp-password mode
                // owes us one. A password that does come back is still surfaced
                // rather than dropped: it can only mean the credential was rotated,
                // and discarding the sole copy is what locks the account out.
                if (mode === PasswordResetMode.EMAIL) {
                    resolve(tempPW || "");
                    return;
                }
                if (!tempPW) {
                    reject(new PasswordResetMissingTempPasswordError());
                    return;
                }
                resolve(tempPW);
            })
                .catch((error) => {
                if (error.response?.status === 403 || error.response?.status === 401) {
                    reject(new NoAuthorizedUser());
                    return;
                }
                reject(new PasswordResetError());
            });
        });
    }
    /**
     * expose the current authenticated user.
     */
    getCurrentAccount() {
        return this.currentAccount.getValue();
    }
    /**
     * The current account's Firebase custom token, re-minted through the account endpoint when
     * it has expired or is about to. Custom tokens live for an hour and are otherwise only minted
     * at login, so a long-lived tab would keep sending a dead one.
     * @returns undefined when no account is signed in.
     * @throws the account request's error when the refresh fails.
     * @throws {UnauthenticatedError} If the signed-in user changes while the refresh is in flight.
     */
    async getFreshFirebaseCustomToken() {
        const account = this.getCurrentAccount();
        if (!account) {
            return undefined;
        }
        if (!this.isFirebaseCustomTokenStale(account.firebaseCustomAuthToken)) {
            return account.firebaseCustomAuthToken;
        }
        // Concurrent callers for the same user share one refresh rather than each hitting the
        // account endpoint; a different user never joins another's.
        const userId = account.user.id;
        if (this.customTokenRefresh?.userId !== userId) {
            const promise = this.refreshFirebaseCustomToken(userId).finally(() => {
                if (this.customTokenRefresh?.promise === promise) {
                    this.customTokenRefresh = undefined;
                }
            });
            this.customTokenRefresh = { userId, promise };
        }
        return this.customTokenRefresh.promise;
    }
    isFirebaseCustomTokenStale(token) {
        const exp = decodeJwtPayload(token)?.exp;
        return (typeof exp !== "number" || exp * 1000 - Date.now() < FIREBASE_CUSTOM_TOKEN_REFRESH_MARGIN_MS);
    }
    /**
     * Writes the new token onto the current account without republishing it: every emission
     * re-warms caches and re-dispatches Redux, and no subscriber reads this token.
     */
    async refreshFirebaseCustomToken(userId) {
        const { data } = await axios.get(`${this.sdk.urls.burnsBaseUrl}/account`);
        const account = this.getCurrentAccount();
        // A sign-out or user switch can land mid-request; never store or hand out another user's token.
        if (data.user?.id !== userId || account?.user.id !== userId) {
            throw new UnauthenticatedError();
        }
        account.firebaseCustomAuthToken = data.firebaseCustomAuthToken;
        return data.firebaseCustomAuthToken;
    }
    addAuthorizationHeader() {
        axios.interceptors.request.use((config) => {
            return this.buildAuthorizationHeaderConfig(config);
        });
    }
    buildAuthorizationHeaderConfig(config) {
        let token;
        if (this.temporaryToken) {
            token = this.temporaryToken;
        }
        else {
            token = this.credentials.token.get();
        }
        if (token) {
            return {
                ...config,
                headers: {
                    ...config.headers,
                    Authorization: `Bearer ${token}`
                }
            };
        }
        return config;
    }
    /**
     * @return: the current token used for authentication, if auth'd. Else, undefined.
     */
    getAuthorizationToken() {
        return this.credentials.token.get();
    }
}
export default Authentication;
//# sourceMappingURL=index.js.map